import { useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import type { Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { Button, EmptyState, Input, LoadingState, MonoLabel, Star } from '@/component';
import { useBillingModals, useBillingSummary, useFavourites, usePack, type PackDetail } from '@/hook';
import { formatUsd } from '@/lib/money';
import { formatDuration } from '@/lib/time';
import { lockedPackNote } from './LibraryPage';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** "In your plan · 38 songs", or why it is not. */
export function packEyebrow(pack: Pick<PackDetail, 'includedInPlans' | 'inPlan' | 'priceCents' | 'songCount'>): string {
  const free = pack.includedInPlans.includes('free');
  const access = free ? 'Free with every account' : pack.inPlan ? 'In your plan' : lockedPackNote(pack);
  return `${access} · ${plural(pack.songCount, 'song')}`;
}

/** /library/packs/:packId: the pack's song table with a search scoped to it.
 * Songs link to their track page only when the caller can play the pack; a
 * pack sold outright carries its Buy button. */
export function PackPage({ packId }: { packId: Id<'packs'> }) {
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  const { favourites, toggle: toggleFavourite } = useFavourites();
  const { openCheckout } = useBillingModals();
  const billing = useBillingSummary();
  const pack = usePack(packId, search);
  const forSale = pack && !pack.inPlan && pack.priceCents !== null ? pack.priceCents : null;

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    setSearch(draft);
  }

  return (
    <div className="pt-10">
      <Link to="/library" className="hover:text-ink">
        <MonoLabel tone="mute">← Library</MonoLabel>
      </Link>
      {!pack ? (
        <LoadingState />
      ) : (
        <>
          <div className="mt-[26px] flex flex-wrap items-end justify-between gap-7">
            <div className="flex flex-wrap items-center gap-6">
              {pack.coverUrl ? (
                <img src={pack.coverUrl} alt="" className="h-[120px] w-[120px] shrink-0 rounded-lg border border-line object-cover" />
              ) : (
                <div className="h-[120px] w-[120px] shrink-0 rounded-lg border border-line bg-stripes" />
              )}
              <div>
                <MonoLabel tone="acc" size="md" className="block">
                  {packEyebrow(pack)}
                </MonoLabel>
                <h1 className="mt-3 text-[42px] font-semibold leading-[1.04] tracking-[-0.035em]">{pack.name}</h1>
                <p className="mt-2 text-[15px] text-mute">
                  {pack.publishedAt === null
                    ? 'Not published'
                    : `Added ${new Date(pack.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-[10px]">
              {forSale !== null && billing?.checkout.open && (
                <Button
                  className="px-[18px] py-[11px]"
                  onClick={() => openCheckout({ kind: 'pack', pack: { _id: pack._id, name: pack.name, priceCents: forSale, songCount: pack.songCount } })}
                >
                  Buy for {formatUsd(forSale)}
                </Button>
              )}
              <form onSubmit={submitSearch} className="flex gap-[10px]">
                <Input
                  aria-label="Search this pack"
                  placeholder="Search this pack"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  className="h-[42px] w-[230px] max-w-full text-sm"
                />
                <Button type="submit" variant="outline" size="sm" className="px-4">
                  Search
                </Button>
              </form>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)_62px_26px] gap-[18px] border-b border-line pb-3">
            <MonoLabel size="md">Song</MonoLabel>
            <MonoLabel size="md">Artist</MonoLabel>
            <MonoLabel size="md" className="text-right">
              Time
            </MonoLabel>
            <span />
          </div>
          {pack.songs.length === 0 ? (
            <EmptyState title={search ? 'No songs match.' : 'This pack is empty.'} />
          ) : (
            pack.songs.map((song) => (
              <div
                key={song._id}
                className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)_62px_26px] items-center gap-[18px] border-b border-stripeA py-4"
              >
                {pack.inPlan ? (
                  <Link
                    to="/tracks/$trackId"
                    params={{ trackId: song._id }}
                    className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em] text-ink hover:text-acc"
                  >
                    {song.title}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em]">{song.title}</span>
                )}
                <div className="truncate text-sm text-mute">{song.artist}</div>
                <div className="text-right font-mono text-xs tabular-nums text-mute">
                  {song.durationMs !== null ? formatDuration(song.durationMs) : ''}
                </div>
                <Star on={favourites.has(song._id)} onToggle={() => toggleFavourite(song._id)} />
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
